import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faBus,
    faSuitcaseRolling,
    faTrain,
    faTaxi,
} from '@fortawesome/pro-regular-svg-icons';
import type ItineraryPackage from './ItineraryPackage';
import { type PaxType } from '@utils/types';
import { type Gender } from '@store/form';

export type CardLabel = {
    title: string;
    backgroundColor?: string;
    color?: string;
    icon?: IconDefinition;
    order?: number;
};

export interface GenderLimitation {
    gender: Gender;
    ignorable: boolean;
    message: string;
}

export const CardLabelColorMaps: Record<string, string> = {
    '#FFFFFF': 'var(--blue-400)',
    '#FFD0C7': 'var(--red-700)',
    '#CDF2B6': 'var(--green-700)',
    '#FFEBB3': 'var(--yellow-700)',
    '#C2E0FF': 'var(--blue-700)',
    '#F0F4F7': 'var(--cool-gray-600)',
};

export type CardDescription = CardLabel & { description: string };

export type VerifyWarningTypes =
    | 'priceIncrease'
    | 'priceDecrease'
    | 'timeChange'
    | 'unknown';
export type VerifyWarningMessage = {
    message: string;
    type: VerifyWarningTypes;
};

export interface OptionalServiceItem {
    /** The price
     *
     * @type {number}
     */
    price: number;

    /** The label
     *
     * @type {string}
     */
    label: string;

    /** The id
     *
     * @type {string}
     */
    id: string | 'none';
}

export interface FreeServiceItem {
    /** The price
     *
     * @type {number}
     */
    price: number;

    /** The label
     *
     * @type {string}
     */
    label: string;

    /** The id
     *
     * @type {string}
     */
    id: number;
}

export interface OptionalService {
    itineraryPackage?: ItineraryPackage;
    title: string;
    selectedService?: OptionalServiceItem;
    services: OptionalServiceItem[];
}

export interface FreeService {
    itineraryPackage?: ItineraryPackage;
    selectedServices?: FreeServiceItem[];
    title: string;
    services: FreeServiceItem[];
}

export interface PassengerFare {
    paxType: PaxType;

    /** The price with discount applied
     *
     * @type {number}
     */
    price: number;

    /** The discount
     *
     * @type {number}
     */
    discount: number;
}
export enum ExtraInfoIcons {
    baggage = 'baggage',
    busVip = 'busVip',
    taxiVip = 'taxiVip',
    trainClass = 'trainClass',
}

export enum ExtraInfoLang {
    fa = 'fa',
    en = 'en',
}

export const extraInfoIconMapper: Record<ExtraInfoIcons, IconDefinition> = {
    [ExtraInfoIcons.baggage]: faSuitcaseRolling,
    [ExtraInfoIcons.trainClass]: faTrain,
    [ExtraInfoIcons.busVip]: faBus,
    [ExtraInfoIcons.taxiVip]: faTaxi,
};

export interface PassengerCount {
    adultCount: number;
    childCount: number;
    infantCount: number;
    foreignerCount?: number; // Behold: us this field just for refetch bills in train
    infantSeatCount?: number;
}

// verify
export interface CancellationTerm {
    title: string;
    amount: string;
    isFull?: boolean;
    isZero?: boolean;
    specifiedByProvider?: boolean;
}

export interface ItineraryPackageLoaderConfig {
    url?: string;
}

export enum PrivacyItems {
    All = 'all',
    None = 'none',
    Authorized = 'authorized',
    Normal = 'normal',
    Organizational = 'organizational',
}

export enum TrackingType {
    Presell = 'presell',
    Price = 'price',
    Capacity = 'capacity',
}
